/**
 * Maintenance: list and status board, report/edit issue, detail with status workflow,
 * contractor, costs (linked expenses), photos/invoices and history.
 */
import { MAINTENANCE_STATUSES, PRIORITIES } from "@shared/constants.ts";
import {
  maintenanceCostSchema,
  maintenanceSchema,
  maintenanceStatusSchema,
} from "@shared/schemas/operations.ts";
import { Card, Col, Row, Table } from "react-bootstrap";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Attachments } from "../../components/Attachments.tsx";
import { type AuditEntry, ChangeList } from "../../components/AuditHistory.tsx";
import { EntityForm } from "../../components/forms/EntityForm.tsx";
import {
  DateField,
  DateTimeField,
  MoneyField,
  SelectField,
  TextAreaField,
  TextField,
} from "../../components/forms/fields.tsx";
import { ModalFormButton } from "../../components/forms/ModalForm.tsx";
import { type Column, DataTable } from "../../components/ui/DataTable.tsx";
import { FilterBar, FilterSelect, SearchInput, ViewTabs } from "../../components/ui/Filters.tsx";
import {
  Can,
  ConfirmButton,
  DefinitionList,
  EmptyState,
  IconButtonLink,
  Money,
  PageHeader,
  QueryState,
  Section,
  StatusBadge,
} from "../../components/ui/kit.tsx";
import { api, type Params, withQuery } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useFormat } from "../../lib/format.ts";
import { toOptions, useLookups } from "../../lib/lookups.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { useSortState, useUrlState } from "../../lib/url-state.ts";

export interface MaintenanceRow {
  id: string;
  propertyId: string;
  tenancyId: string | null;
  title: string;
  description: string;
  reportedDate: string;
  reportedBy: string;
  priority: string;
  status: string;
  contractorId: string | null;
  contractorName: string | null;
  appointmentAt: string | null;
  quotedPence: number | null;
  costPence: number | null;
  completedDate: string | null;
  notes: string;
  addressLine1: string;
  postcode: string;
  expensesTotalPence: number;
}

export function MaintenanceTable({ filters, compact }: { filters: Params; compact?: boolean }) {
  const f = useFormat();
  const { sort, onSort } = useSortState();
  const q = useApi<MaintenanceRow[]>("/api/maintenance", { ...filters, sort: sort.sort, dir: sort.dir });
  const columns: Column<MaintenanceRow>[] = [
    {
      key: "title",
      header: "Issue",
      cell: (m) => (
        <>
          <span className="fw-semibold">{m.title}</span>
          {!compact && <div className="small text-muted">{m.addressLine1}</div>}
        </>
      ),
    },
    { key: "reported", header: "Reported", sort: "reported", cell: (m) => f.date(m.reportedDate) },
    ...(compact
      ? []
      : [
          {
            key: "priority",
            header: "Priority",
            sort: "priority",
            cell: (m: MaintenanceRow) => <StatusBadge status={m.priority} />,
          },
          { key: "contractor", header: "Contractor", cell: (m: MaintenanceRow) => m.contractorName ?? "—" },
          {
            key: "cost",
            header: "Cost",
            align: "end" as const,
            cell: (m: MaintenanceRow) => <Money pence={m.costPence} kind="expense" />,
          },
        ]),
    {
      key: "status",
      header: "Status",
      sort: "status",
      cell: (m) => <StatusBadge status={m.status} label={f.label(MAINTENANCE_STATUSES, m.status)} />,
    },
  ];
  return (
    <QueryState query={q}>
      {(rows) => (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(m) => m.id}
          rowLink={(m) => `/maintenance/${m.id}`}
          sort={sort}
          onSort={onSort}
          empty={
            compact ? (
              <p className="text-muted small p-3 mb-0">No maintenance issues.</p>
            ) : (
              <EmptyState
                icon="tool"
                title="No maintenance issues"
                action={
                  <IconButtonLink
                    to={withQuery("/maintenance/new", { propertyId: filters.propertyId as string })}
                    icon="plus"
                  >
                    Report an issue
                  </IconButtonLink>
                }
              >
                Report issues and track them through scheduling, contractor work, costs and completion.
              </EmptyState>
            )
          }
        />
      )}
    </QueryState>
  );
}

function MaintenanceBoard({ filters }: { filters: Params }) {
  const f = useFormat();
  const q = useApi<MaintenanceRow[]>("/api/maintenance", { ...filters, status: "open" });
  const columns = MAINTENANCE_STATUSES.filter((s) => !["completed", "cancelled"].includes(s.value));
  return (
    <div className="kanban p-3">
      {columns.map((col) => {
        const items = (q.data ?? []).filter((m) => m.status === col.value);
        return (
          <div key={col.value}>
            <div className="fw-semibold small text-uppercase text-muted mb-2">
              {col.label} ({items.length})
            </div>
            {items.map((m) => (
              <Card key={m.id} className="mb-2">
                <Card.Body className="p-2">
                  <Link to={`/maintenance/${m.id}`} className="fw-semibold d-block">
                    {m.title}
                  </Link>
                  <div className="small text-muted">{m.addressLine1}</div>
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <StatusBadge status={m.priority} />
                    <span className="small text-muted">{f.date(m.reportedDate)}</span>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export function MaintenanceListPage() {
  const { get } = useUrlState();
  const lookups = useLookups();
  const layout = get("layout") || "list";
  const filters: Params = { propertyId: get("propertyId"), contractorId: get("contractorId"), q: get("q") };
  return (
    <>
      <PageHeader
        title="Maintenance"
        subtitle="Repairs and issues from report to completion"
        actions={
          <Can permission="maintenance:write">
            <IconButtonLink to="/maintenance/new" icon="plus">
              Report issue
            </IconButtonLink>
          </Can>
        }
      />
      <Card>
        <FilterBar
          actions={
            <ViewTabs
              param="layout"
              defaultValue="list"
              options={[
                { value: "list", label: "List" },
                { value: "board", label: "Board" },
              ]}
            />
          }
        >
          {layout === "list" && (
            <ViewTabs
              param="status"
              defaultValue="open"
              options={[
                { value: "open", label: "Open" },
                { value: "closed", label: "Closed" },
                { value: "all", label: "All" },
              ]}
            />
          )}
          <SearchInput placeholder="Search issues" />
          <FilterSelect param="propertyId" label="Property" options={toOptions(lookups?.properties)} />
          <FilterSelect param="contractorId" label="Contractor" options={toOptions(lookups?.contractors)} />
        </FilterBar>
        {layout === "board" ? (
          <MaintenanceBoard filters={filters} />
        ) : (
          <MaintenanceTable
            filters={{ ...filters, status: get("status") === "all" ? "" : get("status") || "open" }}
          />
        )}
      </Card>
    </>
  );
}

export function MaintenanceFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const lookups = useLookups();
  return (
    <EntityForm
      schema={maintenanceSchema}
      resource="/api/maintenance"
      id={id}
      title={id ? "Edit maintenance issue" : "Report maintenance issue"}
      breadcrumbs={[{ label: "Maintenance", to: "/maintenance" }, { label: id ? "Edit" : "New" }]}
      defaults={{
        propertyId: params.get("propertyId") ?? "",
        tenancyId: params.get("tenancyId") ?? "",
        title: "",
        description: "",
        reportedDate: new Date().toISOString().slice(0, 10),
        reportedBy: "",
        priority: "normal",
        status: "reported",
        contractorId: "",
        appointmentAt: "",
        quotedPence: "",
        costPence: "",
        completedDate: "",
        notes: "",
      }}
      toForm={(r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v === null ? "" : v]))}
      redirect={(saved) => `/maintenance/${saved.id}`}
      cancelTo={id ? `/maintenance/${id}` : "/maintenance"}
    >
      {(form) => {
        const propertyId = form.watch("propertyId");
        return (
          <Row>
            <Col md={6}>
              <SelectField
                name="propertyId"
                label="Property"
                options={toOptions(lookups?.properties)}
                placeholder="Choose a property"
                required
              />
            </Col>
            <Col md={6}>
              <SelectField
                name="tenancyId"
                label="Tenancy"
                options={toOptions(lookups?.tenancies, (t) => t.propertyId === propertyId)}
                placeholder="None"
              />
            </Col>
            <Col md={8}>
              <TextField name="title" label="Issue" required placeholder="e.g. Boiler not heating water" />
            </Col>
            <Col md={4}>
              <SelectField name="priority" label="Priority" options={PRIORITIES} />
            </Col>
            <Col md={12}>
              <TextAreaField name="description" label="Description" />
            </Col>
            <Col md={4}>
              <DateField name="reportedDate" label="Date reported" required />
            </Col>
            <Col md={4}>
              <TextField name="reportedBy" label="Reported by" />
            </Col>
            <Col md={4}>
              <SelectField name="status" label="Status" options={MAINTENANCE_STATUSES} />
            </Col>
            <Col md={4}>
              <SelectField
                name="contractorId"
                label="Contractor"
                options={toOptions(lookups?.contractors)}
                placeholder="Not assigned"
              />
            </Col>
            <Col md={4}>
              <DateTimeField name="appointmentAt" label="Appointment" />
            </Col>
            <Col md={4}>
              <MoneyField name="quotedPence" label="Quote" />
            </Col>
            {id && (
              <Col md={4}>
                <DateField name="completedDate" label="Completed on" />
              </Col>
            )}
            <Col md={12}>
              <TextAreaField name="notes" label="Notes" rows={2} />
            </Col>
          </Row>
        );
      }}
    </EntityForm>
  );
}

interface MaintenanceDetail extends MaintenanceRow {
  expenses: Array<{
    id: string;
    paidDate: string;
    amountPence: number;
    description: string;
    categoryName: string;
  }>;
  history: AuditEntry[];
}

export function MaintenanceDetailPage() {
  const { id } = useParams();
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const lookups = useLookups();
  const { can } = useAuth();
  const q = useApi<MaintenanceDetail>(`/api/maintenance/${id}`);
  const repairs = lookups?.categories.find((c) => c.label === "Repairs & maintenance")?.id ?? "";
  return (
    <QueryState query={q}>
      {(m) => (
        <>
          <PageHeader
            title={m.title}
            subtitle={
              <>
                <Link to={`/properties/${m.propertyId}`}>{m.addressLine1}</Link> ·{" "}
                <StatusBadge status={m.status} label={f.label(MAINTENANCE_STATUSES, m.status)} />{" "}
                <StatusBadge status={m.priority} label={`${m.priority} priority`} />
              </>
            }
            breadcrumbs={[{ label: "Maintenance", to: "/maintenance" }, { label: m.title }]}
            actions={
              <>
                {can("maintenance:write") && (
                  <ModalFormButton
                    label="Update status"
                    buttonSize="md"
                    variant="primary"
                    title="Update status"
                    schema={maintenanceStatusSchema}
                    defaults={{ status: m.status, note: "" }}
                    onSubmit={(v) => api.post(`/api/maintenance/${m.id}/status`, v)}
                  >
                    {() => (
                      <>
                        <SelectField name="status" label="Status" options={MAINTENANCE_STATUSES} />
                        <TextAreaField name="note" label="Note (added to the log)" rows={2} />
                      </>
                    )}
                  </ModalFormButton>
                )}
                {can("expenses:write") && (
                  <ModalFormButton
                    label="Record cost"
                    buttonSize="md"
                    title="Record cost"
                    schema={maintenanceCostSchema}
                    defaults={{
                      amountPence: m.quotedPence ?? "",
                      paidDate: new Date().toISOString().slice(0, 10),
                      categoryId: repairs,
                      description: m.title,
                      reference: "",
                    }}
                    onSubmit={(v) => api.post(`/api/maintenance/${m.id}/costs`, v)}
                  >
                    {() => (
                      <>
                        <p className="small text-muted">
                          This creates an expense linked to this issue
                          {m.contractorName ? ` and ${m.contractorName}` : ""}.
                        </p>
                        <MoneyField name="amountPence" label="Amount" required />
                        <DateField name="paidDate" label="Date paid" required />
                        <SelectField
                          name="categoryId"
                          label="Expense category"
                          options={toOptions(lookups?.categories, (c) => !c.isArchived)}
                          required
                        />
                        <TextField name="description" label="Description" required />
                        <TextField name="reference" label="Invoice reference" />
                      </>
                    )}
                  </ModalFormButton>
                )}
                <Can permission="maintenance:write">
                  <IconButtonLink to={`/maintenance/${m.id}/edit`} icon="edit" variant="outline-primary">
                    Edit
                  </IconButtonLink>
                </Can>
                <Can permission="maintenance:delete">
                  <ConfirmButton
                    size="md"
                    title="Delete this issue?"
                    body="Linked expenses are kept."
                    onConfirm={async () => {
                      await api.delete(`/api/maintenance/${m.id}`);
                      await invalidate();
                      navigate("/maintenance");
                    }}
                  >
                    Delete
                  </ConfirmButton>
                </Can>
              </>
            }
          />
          <Row>
            <Col lg={8}>
              <Section title="Details">
                <DefinitionList
                  items={[
                    ["Reported", `${f.date(m.reportedDate)}${m.reportedBy ? ` by ${m.reportedBy}` : ""}`],
                    [
                      "Contractor",
                      m.contractorId ? (
                        <Link key="c" to={`/contractors/${m.contractorId}`}>
                          {m.contractorName}
                        </Link>
                      ) : (
                        "Not assigned"
                      ),
                    ],
                    ["Appointment", f.dateTime(m.appointmentAt)],
                    ["Quote", f.money(m.quotedPence)],
                    ["Cost", <Money key="c" pence={m.costPence} kind="expense" />],
                    ["Completed", f.date(m.completedDate)],
                    [
                      "Tenancy",
                      m.tenancyId ? (
                        <Link key="t" to={`/tenancies/${m.tenancyId}`}>
                          View tenancy
                        </Link>
                      ) : (
                        "—"
                      ),
                    ],
                  ]}
                />
                {m.description && <p style={{ whiteSpace: "pre-wrap" }}>{m.description}</p>}
                {m.notes && (
                  <>
                    <h3 className="h6">Log</h3>
                    <p className="small mb-0" style={{ whiteSpace: "pre-wrap" }}>
                      {m.notes}
                    </p>
                  </>
                )}
              </Section>
              <Section title="Costs" flush>
                {m.expenses.length === 0 ? (
                  <p className="small text-muted p-3 mb-0">
                    No costs recorded. Use “Record cost” to add the invoice as an expense.
                  </p>
                ) : (
                  <Table className="mb-0">
                    <tbody>
                      {m.expenses.map((e) => (
                        <tr key={e.id}>
                          <td>{f.date(e.paidDate)}</td>
                          <td>
                            <Link to={`/expenses/${e.id}`}>{e.description}</Link>
                          </td>
                          <td>{e.categoryName}</td>
                          <td className="text-end">
                            <Money pence={e.amountPence} kind="expense" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Section>
              <Section title="History">
                {m.history.map((h) => (
                  <div key={h.id} className="border-bottom pb-2 mb-2 small">
                    <span className="text-muted">
                      {f.dateTime(h.createdAt)} · {h.userName ?? "System"}
                    </span>
                    <ChangeList entry={h} />
                  </div>
                ))}
              </Section>
            </Col>
            <Col lg={4}>
              <Attachments entityType="maintenance" entityId={m.id} title="Photos & invoices" />
            </Col>
          </Row>
        </>
      )}
    </QueryState>
  );
}
